import { useQuery } from '@apollo/client';
import { useCallback, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { z } from 'zod';

import { eluveAdminHasuraContext } from '@eluve/apollo-client';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  LoadingIndicator,
  Stack,
  Text,
} from '@eluve/components';

import { useProvisionAndNavigate } from '../../command-palette/useProvisionAndNavigate';

import { ProvisionPrompt, ProvisionWarning } from './ProvisionPrompt';
import { getAdminTenantsSummaryFilterQuery } from './query';

const uuidSchema = z.string().uuid();

/**
 * True when `returnTo` is a same-origin admin path scoped to `tenantId`.
 *
 * The path is parsed against the current origin so `..`/`.` segments are
 * canonicalized before matching — a raw `startsWith` can be fooled by
 * `/admin/organizations/<A>/../<B>/manage`, which the router normalizes to
 * tenant B on navigation. Requiring the literal `admin` / `organizations` /
 * `<tenantId>` leading segments closes that bait-and-switch and, via the
 * same-origin check, an open redirect to another host.
 */
const isReturnToForTenant = (
  returnTo: string | null,
  tenantId: string | null,
): boolean => {
  if (!returnTo || !tenantId) {
    return false;
  }
  let url: URL;
  try {
    url = new URL(returnTo, window.location.origin);
  } catch {
    return false;
  }
  if (url.origin !== window.location.origin) {
    return false;
  }
  const [admin, organizations, id] = url.pathname.split('/').filter(Boolean);
  return (
    admin === 'admin' && organizations === 'organizations' && id === tenantId
  );
};

/**
 * Deep-link provisioning dialog rendered on the admin org list. When
 * {@link TenantGuard} bounces an unprovisioned tenant page it lands here with
 * `provisionTenantId` + `returnTo` search params; this surfaces the same
 * `Cancel` / `Provision and open` panel the command palette uses. Confirm
 * provisions the current user for that tenant and routes them to the page they
 * were trying to reach; Cancel clears the params and leaves them on the list.
 *
 * `returnTo` is validated to the provisioned tenant's own
 * `/admin/organizations/<id>` path so a crafted link can't turn this into an
 * open redirect or a cross-tenant bait-and-switch. The appointment deep-link
 * flow keeps its own {@link QuickProvisionDialog}.
 */
export const TenantProvisionDialog = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const provisionTenantId = searchParams.get('provisionTenantId');
  const returnTo = searchParams.get('returnTo');

  // Only ever provision for a real tenant id, and only ever navigate back to
  // that same tenant's page — see isReturnToForTenant for why a canonicalized
  // segment match (not a string prefix) is required. The TenantGuard redirect
  // always sets returnTo under /admin/organizations/<tenantId>, so this never
  // rejects the real flow.
  const isValidTenantId = uuidSchema.safeParse(provisionTenantId).success;
  const isValidReturnTo =
    isValidTenantId && isReturnToForTenant(returnTo, provisionTenantId);
  const isOpen = isValidTenantId && isValidReturnTo;

  const { data, loading, error } = useQuery(getAdminTenantsSummaryFilterQuery, {
    variables: {
      // Hide soft-deleted tenants, matching the org list and command palette —
      // otherwise a crafted deep link to a deleted tenant id would resolve its
      // name here and offer to provision it.
      filter: {
        _and: [
          { id: { _eq: provisionTenantId ?? '' } },
          { deletedAt: { _isNull: true } },
        ],
      },
      limit: 1,
    },
    context: eluveAdminHasuraContext,
    skip: !isOpen,
  });

  const tenant = data?.adminTenants?.[0];
  const tenantName = tenant?.name ?? '';
  const isProvisioned = tenant?.isCurrentUserProvisioned ?? false;

  // The dialog opens synchronously from the URL params, but the org name is
  // fetched async. Never render the ProvisionPrompt with a blank name: show a
  // loading state while the query is in flight, and an error state if it fails
  // or returns nothing (a cold direct-load hits the former; a failed admin-role
  // token acquisition hits the latter — see ENG-4429 follow-up).
  const hasFailed = !loading && (Boolean(error) || !tenant);

  const { run: provisionAndNavigate, status: provisionStatus } =
    useProvisionAndNavigate();
  const isProvisioning =
    provisionStatus === 'provisioning' || provisionStatus === 'switching';

  useEffect(() => {
    // Already-provisioned edge case: `run` short-circuits to navigate when it
    // detects membership, so invoking it here just routes the user through
    // without showing the prompt. Mirrors QuickProvisionDialog's auto-nav.
    if (isOpen && isProvisioned && provisionTenantId && returnTo) {
      void provisionAndNavigate({
        tenantId: provisionTenantId,
        targetPath: returnTo,
      });
    }
  }, [
    isOpen,
    isProvisioned,
    provisionTenantId,
    returnTo,
    provisionAndNavigate,
  ]);

  const clearParams = useCallback(() => {
    setSearchParams((prev) => {
      prev.delete('provisionTenantId');
      prev.delete('returnTo');
      return prev;
    });
  }, [setSearchParams]);

  const handleProvision = async () => {
    if (!provisionTenantId || !returnTo) {
      return;
    }
    // Capacity / errors are surfaced by the hook's own toasts.
    await provisionAndNavigate({
      tenantId: provisionTenantId,
      targetPath: returnTo,
    });
  };

  const handleOpenChange = (open: boolean) => {
    if (!open) {
      clearParams();
    }
  };

  let body: React.ReactNode;
  if (loading) {
    body = (
      <Stack className="items-center p-6" gap={3}>
        <LoadingIndicator />
        <Text>Loading organization</Text>
      </Stack>
    );
  } else if (hasFailed) {
    body = (
      <Stack className="p-6" gap={4} align="stretch">
        <ProvisionWarning
          title="We couldn't load this organization."
          detail="Check your connection and try again."
        />
        <Stack direction="horizontal" gap={2} justify="end">
          <Button variant="outline" onClick={clearParams}>
            Close
          </Button>
        </Stack>
      </Stack>
    );
  } else {
    body = (
      <ProvisionPrompt
        title="You don't have access to this organization:"
        detail={tenantName}
        confirmLabel="Provision and open"
        onConfirm={handleProvision}
        onCancel={clearParams}
        running={isProvisioning}
      />
    );
  }

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        size="lg"
        className="overflow-hidden rounded-xl! p-0"
        showCloseButton={false}
      >
        <DialogHeader className="sr-only">
          <DialogTitle>Provision organization access</DialogTitle>
          <DialogDescription>
            Provision your account for this organization to open the page you
            requested.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
};
